import React, { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import * as S from './DesktopFilters.styles';
import { DesktopFilterAccordion } from '@/components/Filters/DesktopFilters/DesktopFilterAccordion';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { Region } from '@/api/types/regions.types';
import { QUERY } from '@/constants/common';
import { KeywordsSource } from '@/components/SearchPanel/components/SearchSuggestions.types';
import { AccordionRef } from '@/components/UI/Accordion/Accordion';
import { SearchResultsPageTestIds } from '@/__e2e__/testIds/search-results-page';

interface Props {
  currentRegion: Region | null;
  regions?: Region[];
}

export const RegionDesktopFilter: FC<Props> = ({
  currentRegion,
  regions = [],
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { clearUrl, queryString } = useUrlAndHashFromPath();
  const [searchValue, setSearchValue] = useState('');
  const accordionRef = useRef<AccordionRef>(null);

  function getHref(value: string | undefined) {
    const searchParams = new URLSearchParams(queryString);

    const key = `${QUERY.q}[${QUERY.region_id}]`;

    searchParams.delete(QUERY.user_id);
    searchParams.set(QUERY.keywords_source, KeywordsSource.Typewritten);

    if (value !== undefined) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }

    return searchParams.size
      ? `${clearUrl}?${searchParams.toString()}`
      : clearUrl;
  }

  function handleClear() {
    const href = getHref(undefined);
    router.push(href, undefined, { scroll: false });
  }

  const options = regions.filter(region =>
    region.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  return (
    <DesktopFilterAccordion
      ref={accordionRef}
      dataTestId={SearchResultsPageTestIds.desktopFilters.region}
      title={t('common.city')}
      value={currentRegion?.name}
      onClear={handleClear}
      search={
        <S.SearchInput
          $hasBottomMargin
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          placeholder={t('common.search')}
          autoCapitalize="none"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          type="search"
        />
      }
    >
      {options.length > 0 ? (
        options.map(region => {
          const isSelected = currentRegion?.id === region.id;
          return (
            <S.LinkOption
              key={region.id}
              href={getHref(region.legacyResourceId)}
              onClick={() => accordionRef.current?.close()}
              $isActive={isSelected}
              dataTestId={SearchResultsPageTestIds.desktopFilters.regionOption}
            >
              <S.RadioButton $isActive={isSelected} />
              <S.LinkOptionText>{region.name}</S.LinkOptionText>
            </S.LinkOption>
          );
        })
      ) : (
        <S.NothingFound>{t('common.nothing_found')}</S.NothingFound>
      )}
    </DesktopFilterAccordion>
  );
};
